import { useCallback, useEffect, useState } from "react";
import { UserPlus, Shield, Crown, User, Trash2 } from "lucide-react";
import {
  WORKSPACE_ROLE_PERMISSIONS,
  type WorkspaceAutoJoinSetting,
  type WorkspaceInvite,
  type WorkspaceMember,
  type WorkspaceMembershipRole,
} from "@yagni/shared";
import { Switch } from "../ui/switch.js";
import { Button } from "../ui/button.js";
import { IconButton } from "../ui/icon-button.js";
import { Card } from "../ui/card.js";
import { Pane } from "../ui/pane.js";
import { Avatar } from "../ui/avatar.js";
import { Badge } from "../ui/badge.js";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  InviteByEmail,
  INVITE_ROLE_OPTIONS,
} from "../ui/index.js";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select.js";
import { readJsonError } from "../lib/readJsonError.js";
import {
  useAuthSession,
  useCan,
  useWorkspaceRole,
} from "../hooks/useWorkspaceRole.js";
import { ChevronRightIcon } from "../queue/icons.js";
import {
  ChapterSection,
  ChapterCard,
  ChapterLoading,
} from "../settings/chapter.js";

function RoleBadge({ role }: { role: WorkspaceMembershipRole }) {
  const variants: Record<WorkspaceMembershipRole, "warning" | "info" | "neutral"> = {
    owner: "warning",
    admin: "info",
    member: "neutral",
  };
  const icons: Record<WorkspaceMembershipRole, typeof Crown> = {
    owner: Crown,
    admin: Shield,
    member: User,
  };
  const Icon = icons[role];

  return (
    <Badge size="xs" variant={variants[role]}>
      <Icon size={12} />
      {role}
    </Badge>
  );
}

function InviteStatusBadge({ status }: { status: string }) {
  const variants: Record<string, "warning" | "success" | "error" | "neutral"> = {
    pending: "warning",
    accepted: "success",
    revoked: "error",
    expired: "neutral",
  };

  return (
    <Badge size="xs" variant={variants[status] ?? "neutral"}>
      {status}
    </Badge>
  );
}

export function TeamView({ onMembers }: { onMembers?: (members: WorkspaceMember[]) => void } = {}) {
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [invites, setInvites] = useState<WorkspaceInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Mirror the backend gates so members never see controls that would 403:
  // role changes / removal are manage-members, inviting is invite-members,
  // and anything touching an owner (or granting owner) is owner-only.
  const session = useAuthSession();
  const viewerRole = useWorkspaceRole();
  const canManageMembers = useCan("manage-members");
  const canInvite = useCan("invite-members");
  const isOwner = viewerRole === "owner";

  // Invite modal state — the form body is the shared <InviteByEmail>, so
  // the parent only tracks open/closed and the error surface. The new-invite
  // callback updates the pending-invites list.
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  // Domain auto-join (admins see it; only owners can flip it — the backend
  // enforces that and a 403 surfaces below the toggle).
  const [autoJoin, setAutoJoin] = useState<WorkspaceAutoJoinSetting | null>(null);
  const [autoJoinDomain, setAutoJoinDomain] = useState<string | null>(null);
  const [autoJoinError, setAutoJoinError] = useState<string | null>(null);
  const [autoJoinSaving, setAutoJoinSaving] = useState(false);

  const loadData = useCallback(async () => {
    setError(null);
    try {
      const [membersRes, invitesRes, autoJoinRes] = await Promise.all([
        fetch("/api/members"),
        fetch("/api/members/invites"),
        fetch("/api/members/auto-join"),
      ]);

      if (membersRes.ok) {
        const data = (await membersRes.json()) as { members: WorkspaceMember[] };
        setMembers(data.members);
        onMembers?.(data.members);
      }

      if (invitesRes.ok) {
        const data = (await invitesRes.json()) as { invites: WorkspaceInvite[] };
        setInvites(data.invites);
      } else if (invitesRes.status === 403) {
        // Non-admin, no invites access — that's fine
        setInvites([]);
      }

      if (autoJoinRes.ok) {
        const data = (await autoJoinRes.json()) as {
          autoJoin: WorkspaceAutoJoinSetting | null;
          eligibleDomain: string | null;
        };
        setAutoJoin(data.autoJoin ?? null);
        setAutoJoinDomain(data.autoJoin?.domain ?? data.eligibleDomain ?? null);
      }
      // Non-admin 403 → autoJoinDomain stays null and the card never renders.
    } catch {
      setError("Failed to load team data");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleAutoJoinToggle = async (enabled: boolean) => {
    if (!autoJoinDomain) return;
    setAutoJoinSaving(true);
    setAutoJoinError(null);
    try {
      const res = await fetch("/api/members/auto-join", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled, defaultRole: autoJoin?.defaultRole ?? "member" }),
      });
      if (res.ok) {
        const data = (await res.json()) as { autoJoin: WorkspaceAutoJoinSetting };
        setAutoJoin(data.autoJoin);
        setAutoJoinDomain(data.autoJoin.domain);
      } else {
        setAutoJoinError(await readJsonError(res));
      }
    } catch {
      setAutoJoinError("Failed to update auto-join");
    } finally {
      setAutoJoinSaving(false);
    }
  };

  useEffect(() => {
    loadData().catch(console.error);
  }, [loadData]);

  const handleRevokeInvite = async (inviteId: string) => {
    const res = await fetch(`/api/members/invites/${inviteId}`, { method: "DELETE" });
    if (res.ok) {
      setInvites((prev) =>
        prev.map((inv) =>
          inv.id === inviteId ? { ...inv, status: "revoked" as const, revokedAt: new Date().toISOString() } : inv,
        ),
      );
    }
  };

  const handleRoleChange = async (userId: string, role: WorkspaceMembershipRole) => {
    const res = await fetch(`/api/members/${userId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ role }),
    });

    if (res.ok) {
      setMembers((prev) =>
        prev.map((m) => (m.userId === userId ? { ...m, role } : m)),
      );
    } else {
      setError(await readJsonError(res));
    }
  };

  const handleRemoveMember = async (userId: string) => {
    const res = await fetch(`/api/members/${userId}`, { method: "DELETE" });
    if (res.ok) {
      setMembers((prev) => prev.filter((m) => m.userId !== userId));
    } else {
      setError(await readJsonError(res));
    }
  };

  const closeModal = () => {
    setShowInviteModal(false);
    setInviteError(null);
  };

  const pendingInvites = invites.filter((i) => i.status === "pending");
  const pastInvites = invites.filter((i) => i.status !== "pending");

  // One visibility record for the section gates below, in document order.
  // Sections carry no number of their own: the settings rail is the running
  // order, so a workspace without auto-join or invites just reads shorter.
  const show = {
    members: true,
    autoJoin: Boolean(autoJoinDomain),
    pending: pendingInvites.length > 0,
    history: pastInvites.length > 0,
    roles: true,
  };

  if (loading) {
    return <ChapterLoading label="Loading team..." />;
  }

  return (
    <div className="body" data-testid="settings-members">
      {error && (
        <Card tone="error" padding="sm" className="mt-4 text-sm">
          {error}
        </Card>
      )}

      <ChapterSection
        label="Members"
        rightLabel={`${members.length} ${members.length === 1 ? "person" : "people"}`}
      />
      <ChapterCard>
        <div className="divide-y divide-[var(--border-subtle)]">
          {members.map((member) => (
            <div key={member.userId} className="flex items-center gap-3 px-4 py-3">
              <Avatar
                fallback={member.displayName.charAt(0).toUpperCase()}
                alt={member.displayName}
                className="w-8 h-8 bg-[var(--accent-muted)] text-[var(--accent)] text-sm font-semibold"
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[var(--text-primary)] truncate">
                  {member.displayName}
                </div>
                <div className="text-xs text-[var(--text-secondary)] truncate">{member.email}</div>
              </div>
              {(() => {
                // Backend rules mirrored: no self-role-change; owner targets
                // (and granting owner) require an owner; members get a badge.
                const isSelf = member.userId === session?.user.id;
                const canEditThisMember =
                  canManageMembers &&
                  !isSelf &&
                  (isOwner || member.role !== "owner");
                if (!canEditThisMember) {
                  return <RoleBadge role={member.role} />;
                }
                const roleOptions = INVITE_ROLE_OPTIONS.filter(
                  (opt) => isOwner || opt.value !== "owner",
                );
                return (
                  <div className="flex items-center gap-2">
                    <Select
                      value={member.role}
                      onValueChange={(v) =>
                        void handleRoleChange(member.userId, v as WorkspaceMembershipRole)
                      }
                    >
                      <SelectTrigger className="w-auto h-7 text-xs px-2">
                        {/* Trigger shows the short label only; the description
                            renders inside the dropdown items. */}
                        <SelectValue>
                          {WORKSPACE_ROLE_PERMISSIONS[member.role].label}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent className="max-w-[28rem]">
                        {roleOptions.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value} textValue={opt.label}>
                            <span className="flex flex-col">
                              <span>{opt.label}</span>
                              <span className="text-xs text-[var(--text-tertiary)] font-normal">
                                {opt.description}
                              </span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <IconButton
                      variant="ghost"
                      size="sm"
                      aria-label="Remove member"
                      title="Remove member"
                      onClick={() => void handleRemoveMember(member.userId)}
                      className="text-[var(--text-tertiary)] hover:text-[var(--error)]"
                    >
                      <Trash2 size={14} />
                    </IconButton>
                  </div>
                );
              })()}
            </div>
          ))}
        </div>
      </ChapterCard>

      {/* Domain auto-join — rendered only when the admin fetch succeeded and
          the workspace has (or the owner's email yields) a company domain. */}
      {show.autoJoin && autoJoinDomain && (
        <>
          <ChapterSection label="Auto-join" />
          <ChapterCard>
            <div className="flex items-start gap-3 px-4 py-3">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[var(--text-primary)]">
                  Let anyone at @{autoJoinDomain} join automatically
                </div>
                <p className="text-xs text-[var(--text-secondary)] mt-1">
                  New sign-ins with a verified {autoJoinDomain} Google
                  Workspace account join this workspace as{" "}
                  {autoJoin?.defaultRole ?? "member"} — no individual invite
                  needed. People who already have a YAGNI workspace still need
                  an invite. Only owners can change this.
                </p>
                {autoJoinError && (
                  <p className="text-xs text-[var(--error)] mt-1" role="alert">
                    {autoJoinError}
                  </p>
                )}
              </div>
              <Switch
                aria-label={`Auto-join for ${autoJoinDomain}`}
                checked={autoJoin?.enabled ?? false}
                disabled={autoJoinSaving}
                onCheckedChange={(checked) => void handleAutoJoinToggle(checked)}
              />
            </div>
          </ChapterCard>
        </>
      )}

      {/* Pending invites */}
      {show.pending && (
        <>
          <ChapterSection
            label="Pending invites"
            rightLabel={String(pendingInvites.length)}
          />
          {/* An open invite waits on a human to accept, so the group reads as
              glass (staged), not printed record. */}
          <Pane material="staged" className="overflow-hidden">
            <div className="divide-y divide-[var(--glass-divider)]">
              {pendingInvites.map((invite) => (
                <div key={invite.id} className="flex items-center gap-3 px-4 py-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-[var(--text-primary)] truncate">{invite.email}</div>
                    <div className="text-xs text-[var(--text-secondary)]">
                      Invited by {invite.invitedByName} &middot; Expires{" "}
                      {new Date(invite.expiresAt).toLocaleDateString()}
                    </div>
                  </div>
                  <RoleBadge role={invite.role} />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => void handleRevokeInvite(invite.id)}
                    className="text-xs text-[var(--error)] hover:text-[var(--error)]"
                  >
                    Revoke
                  </Button>
                </div>
              ))}
            </div>
          </Pane>
        </>
      )}

      {/* Past invites */}
      {show.history && (
        <>
          <ChapterSection label="Invite history" />
          <ChapterCard>
            <div className="divide-y divide-[var(--border-subtle)]">
              {pastInvites.map((invite) => (
                <div key={invite.id} className="flex items-center gap-3 px-4 py-3 opacity-60">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-[var(--text-primary)] truncate">{invite.email}</div>
                    <div className="text-xs font-mono text-[var(--text-secondary)]">
                      {new Date(invite.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                  <RoleBadge role={invite.role} />
                  <InviteStatusBadge status={invite.status} />
                </div>
              ))}
            </div>
          </ChapterCard>
        </>
      )}

      {/* Invite Modal */}
      <Dialog
        open={showInviteModal}
        onOpenChange={(open) => {
          if (!open) closeModal();
        }}
      >
        <DialogContent className="p-0">
          <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border)]">
            <DialogTitle className="font-semibold text-[var(--text-primary)]">
              Invite to workspace
            </DialogTitle>
          </div>

          <div className="px-5 py-4 space-y-4">
            <InviteByEmail
              isAdmin={canInvite}
              onError={setInviteError}
              onInvited={(invite) => setInvites((prev) => [invite, ...prev])}
            />
            {inviteError && (
              <div className="text-sm text-[var(--error)]">{inviteError}</div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Role explanation disclosure */}
      <details className="fold" data-testid="roles-fold">
        <summary>
          <ChevronRightIcon />
          What can each role do?
          <span className="n" />
        </summary>
        <div className="fb space-y-3">
          {(["member", "admin", "owner"] as const).map((role) => {
            const spec = WORKSPACE_ROLE_PERMISSIONS[role];
            return (
              <div key={role} className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <RoleBadge role={role} />
                  <span className="text-sm text-[var(--text-secondary)]">
                    {spec.summary}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </details>

      {canInvite && (
        <div className="acts">
          <button type="button" className="btn secondary sm" onClick={() => setShowInviteModal(true)}>
            <UserPlus size={14} aria-hidden />
            Invite
          </button>
        </div>
      )}
    </div>
  );
}
